import { matchSorter } from 'match-sorter';
import { OperationLog } from '@rossum/mdh-api-client';
import { Dataset } from '../types';

export const DATASET_TYPES = ['manual', 'managed'] as const;
export type DatasetType = (typeof DATASET_TYPES)[number];

export const DATASET_STATUSES = [
  'failed',
  'inProgress',
  'completed',
  'noRecentUpdates',
] as const;
export type DatasetStatus = (typeof DATASET_STATUSES)[number];

export type OperationsByDataset = Partial<Record<string, OperationLog[]>>;

export const getDatasetType = (dataset: Dataset): DatasetType =>
  dataset.metadata.manual ? 'manual' : 'managed';

// Drives both the DatasetTile badge and the status filter; operations are
// sorted newest first.
// The list only loads a limited window of operations, so `noRecentUpdates`
// also covers datasets whose operations fall outside of it.
export const getDatasetStatus = (
  operations: OperationLog[] | undefined
): DatasetStatus => {
  const lastStatus = operations?.[0]?.status;

  if (lastStatus === 'FAILED') return 'failed';
  if (lastStatus === 'IN_PROGRESS' || lastStatus === 'WAITING')
    return 'inProgress';
  if (operations?.some(op => op.status === 'FINISHED')) return 'completed';

  return 'noRecentUpdates';
};

export const filterDatasets = ({
  datasets,
  operationsByDataset,
  search,
  type,
  status,
}: {
  datasets: readonly Dataset[];
  operationsByDataset: OperationsByDataset;
  search: string | undefined;
  type: DatasetType | undefined;
  status: DatasetStatus[] | undefined;
}): Dataset[] => {
  const filtered = datasets.filter(
    dataset =>
      (!type || getDatasetType(dataset) === type) &&
      (!status?.length ||
        status.includes(getDatasetStatus(operationsByDataset[dataset.name])))
  );

  const normalizedSearch = search?.trim();
  if (!normalizedSearch) return filtered;

  return matchSorter(filtered, normalizedSearch, {
    keys: [
      dataset => dataset.metadata.name,
      dataset => dataset.name,
      dataset => dataset.metadata.description,
    ],
    threshold: matchSorter.rankings.CONTAINS,
  });
};
